<template>
  <div>
    <h3 class="heading">Tumor Board</h3>
    <span class="label-span">Add, Edit and Enable/Disable Tumor Boards here</span>
    <AccordionTumorBoard @reloadComponent="reloadComponent" @form-change="$emit('form-change')"
      @reset-confirmation="$emit('reset-confirmation')" />
    <div v-if="tumorBoards?.length" id="tumor-boards-container">
      <h4 class="heading">Current Tumor Boards</h4>
      <div v-for="(item, index) in tumorBoards" :key="item.tb_id">
        <div v-if="isTbEditMode && item.tb_name === editableTb">
          <AccordionTumorBoard :indexOfTB="index" :values="item" @accordionTabClose="handleCollapseTB"
            @reloadComponent="reloadComponent" @form-change="$emit('form-change')"
            @reset-confirmation="$emit('reset-confirmation')" />
        </div>
        <div v-else>
          <div class="current-admin-record-div">
            <div>
              <InputText type="text" v-model="item.tb_name" size="small" class="edit-input-tb" placeholder="Enter Name"
                readonly :disabled=!item.is_active @click="handleEditTB(item.tb_name)" />
            </div>
            <div class="input-switch-div" title="Enable/Disable">
              <InputSwitch v-model="item.is_active" @change="toggleSwitchChange(item)" size="small" />
            </div>
            <div>
              <button class="tb-edit-button " title="Edit" :disabled="!item.is_active"
                @click="handleEditTB(item.tb_name)">
                <span class="material-symbols-outlined icon-button-to-primary">edit</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Dialog v-model:visible="forceDelete" modal header="Info">
      <p>{{ `This tumor board has patients assigned to it. Please reassign patients to a different tumor board from the
        “To Be Scheduled” and “Scheduled” tables before disabling this tumor board.` }}</p>
      <div class="flex gap-2">
        <button class="primary-outlined-button cancel-btn" @click.prevent="handleCloseForceDelete">Cancel</button>
        <!-- TODO: Confirm button may require in future, anytime requirement may change, keeping it in commented section. -->
        <!-- <button class="primary-filled-button save-btn" @click.prevent="handleConfirmForceDelete">Confirm</button> -->
      </div>
    </Dialog>
    <Dialog v-model:visible="isDialogVisible" modal header="Confirmation">
      <p>{{ dynamicDialogContent }}</p>
      <div class="flex gap-2">
        <button class="primary-outlined-button cancel-btn" @click.prevent="handleDialogCancel">Cancel</button>
        <button class="primary-filled-button save-btn" @click.prevent="handleDialogConfirm">Confirm</button>
      </div>
    </Dialog>
    <Toast position="bottom-right" group="br" />
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, defineEmits, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import InputText from 'primevue/inputtext'
import InputSwitch from 'primevue/inputswitch'
import Toast from 'primevue/toast'
import Dialog from 'primevue/dialog'
import { useToast } from 'primevue/usetoast'
import AccordionTumorBoard from './AccordionTumorBoard'
import { getAdminRecordData } from './getAdminRecordData'
import { deleteTumorBoard } from '@/ux3/cards/ManageTumorBoard/deleteTumorBoard'
import { putTumorBoard } from '@/ux3/apis/putTumorBoard.js'


defineEmits(['form-change', 'reset-confirmation'])

const isTbEditMode = ref(false)
const editableTb = ref('')
const patchTBResponse = ref('')
const forceDelete = ref(false)
const editableTbId = ref('')
const dynamicDialogContent = ref('')
const tumorBoardElement = ref({})
const isDialogVisible = ref(false)

const deleteTBResponse = computed(() => store.state.session.deleteTBResponse)
const tumorBoards = computed(() => store.state.session?.tumorBoardTypes?.tb_types)

const store = useStore()
const toast = useToast()

watch(deleteTBResponse, (newDeleteTBResponse) => {
  if (!newDeleteTBResponse) { return }
  if (newDeleteTBResponse?.detail?.includes('schedules associated')) {
    forceDelete.value = true
  } else if (newDeleteTBResponse) {
    forceDelete.value = false
  }
})

onMounted(async () => {
  getTBOnSuccess()
})

const toggleSwitchChange = (element) => {
  editableTbId.value = element.tb_id
  editableTb.value = element.tb_name
  tumorBoardElement.value = element
  dynamicDialogContent.value = element.is_active ? `Do you want to enable ${editableTb.value}?` : `Are you sure you want to disable this tumor board?`
  element.is_active = !element.is_active
  isDialogVisible.value = true
}

const reloadComponent = () => {
  getTBOnSuccess()
}

const handleCloseForceDelete = () => {
  forceDelete.value = false
  getTBOnSuccess()
}

const handleDialogConfirm = async () => {
  if (tumorBoardElement.value?.is_active) {
    const deleteTBResponse = await deleteTumorBoard(tumorBoardElement.value)
    store.dispatch('setDeleteTBResponse', { deleteTBResponse, publish: true })
    if (deleteTBResponse?.detail?.includes('schedules associated')) {
      forceDelete.value = true
    } else {
      forceDelete.value = false
      getTBOnSuccess('Tumor Board has been disabled successfully')
    }
  } else {
    patchTBResponse.value = await putTumorBoard({ tb_id: tumorBoardElement.value?.tb_id })
    getTBOnSuccess('Tumor Board has been enabled successfully')
  }
  isDialogVisible.value = false
}

const handleDialogCancel = () => isDialogVisible.value = false

const getTBOnSuccess = async (message) => {
  if (message) {
    toast.add({ detail: message, group: 'br', life: 3000 })
  }

  const tumorBoardsData = await getAdminRecordData('tumorboards')
  tumorBoardsData.tb_types.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

  store.dispatch('setTumorBoardTypes', { tumorBoardTypes: tumorBoardsData, publish: true })
}

const handleEditTB = (tbName) => {
  isTbEditMode.value = !isTbEditMode.value
  editableTb.value = tbName
}

const handleCollapseTB = () => {
  isTbEditMode.value = !isTbEditMode.value
  editableTb.value = ''
}

//TODO: Handle Confirm on force deletion function may require in future, anytime requirement may change, keeping it in commented section.
// const handleConfirmForceDelete = async () => {
//   const payload = {
//     tb_id: editableTbId.value,
//   }
//   payload.force_disable = true
//   forceDelete.value = false
//   await deleteTumorBoard(payload)
//   getTBOnSuccess('Tumor Board has been disabled successfully')
// }

</script>

<style lang="scss" scoped>
.heading {
  margin-bottom: 1%;
  margin-left: -0.2%;
}

.label-span {
  color: var(--Grey600);
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
}

.edit-input-tb {
  margin-right: 2%;
}

.current-admin-record-div {
  margin-bottom: 20px;
  display: flex;
  width: 60%;
  align-items: center;
}

.input-switch-div {
  margin-left: 3%;
  margin-top: 1%;
}

.tb-edit-button {
  outline: none;
  border: none;
  background-color: transparent;
  transition: all linear 200ms;
  cursor: pointer;
  color: var(--Grey600);
}

*:disabled {
  color: var(--Grey600);
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.p-inputtext {
  cursor: pointer;
}
</style>
